import React, { useState, Suspense, useMemo } from "react";
import StageList from "../stages/StageList";
import TeamList from "../team/TeamList";
import Fixtures from "../stages/Fixtures";
import Standings from "../season/Standings";
import SeasonList from "../season/SeasonList";
import Adjustment from "../season/standings/Adjustment";
import Sanctions from "../season/sanction/Sanctions";
import StatsDashboard from "../season/StatsDashboard";
import useHasFeature from "../common/hooks/useHasFeature";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

export default function DivisionTags({
                                         stages,
                                         setStages,
                                         teams,
                                         setTeams,
                                         activeSeasonData,
                                         division,
                                         games,
                                         setGames,
                                         standings,
                                         seasons,
                                         setSeasons,
                                         sanctions,
                                         waivers = [],
                                         setTotalGames = 0,
                                     }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState("teams");

    const { hasFeature } = useHasFeature();
    const pro = hasFeature("pro_standing");

    const ui = useMemo(() => {
        return {
            shell: {
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
                overflow: "hidden",
            },
            header: {
                padding: "12px 12px 10px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.0)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
            },
            title: {
                margin: 0,
                fontSize: 13,
                fontWeight: 900,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                color: colors.textOnPrimary,
            },
            sub: {
                margin: "6px 0 0",
                fontSize: 12,
                color: colors.textOnPrimary,
                textTransform: "none",
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.secondary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnSecondary,
                whiteSpace: "nowrap",
            },
            tabsWrap: {
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                margin: 0,
                padding: 0,
                listStyle: "none",
            },
            tabBtnBase: {
                borderRadius: 999,
                padding: "8px 12px",
                fontWeight: 900,
                fontSize: 13,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                color: "rgba(20,21,26,0.75)",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                userSelect: "none",
                transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
            },
            tabBtnActive: {
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.12)",
                color: colors.textOnPrimary,
                boxShadow: "0 10px 18px rgba(0,0,0,0.10)",
            },
            tabBtnDisabled: {
                opacity: 0.55,
                cursor: "not-allowed",
                background: "rgba(0,0,0,0.03)",
                color: "rgba(20,21,26,0.55)",
            },
            dot: {
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "rgba(0,0,0,0.20)",
            },
            dotActive: {
                background: "rgba(20,21,26,0.85)",
            },
            body: {
                padding: 14,
            },
        };
    }, [colors.primary, colors.textOnPrimary, colors.secondary, colors.textOnSecondary]);

    const NavItem = ({ id, label, disabled, icon }) => {
        const isActive = activeTab === id;

        return (
            <li>
                <a
                    href={`#${id}`}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!disabled) setActiveTab(id);
                    }}
                    aria-selected={isActive}
                    role="tab"
                    aria-controls={id}
                    style={{
                        ...ui.tabBtnBase,
                        ...(isActive ? ui.tabBtnActive : null),
                        ...(disabled ? ui.tabBtnDisabled : null),
                        cursor: disabled ? "not-allowed" : "pointer",
                    }}
                    className="kbola-tab-btn"
                >
                    <span style={{ ...ui.dot, ...(isActive ? ui.dotActive : null) }} />
                    {icon ? <i className={`fas ${icon}`} /> : null}
                    {label}
                </a>
            </li>
        );
    };

    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <div style={ui.shell}>
                        <div style={ui.header}>
                            <div style={ui.titleRow}>
                                <h3 style={ui.title}>
                                    {t("divisionTags.title")}
                                    {!pro && (
                                        <p style={ui.sub}>
                                            {t("divisionTags.premiumNotice")}
                                        </p>
                                    )}
                                </h3>

                                {pro ? (
                                    <span style={ui.badge}>{t("divisionTags.pro")}</span>
                                ) : (
                                    <span style={ui.badge}>{t("divisionTags.basic")}</span>
                                )}
                            </div>

                            <ul
                                style={ui.tabsWrap}
                                role="tablist"
                                aria-label={t("divisionTags.divisionTabsAriaLabel")}
                            >
                                <NavItem
                                    id="teams"
                                    label={t("divisionTags.teams")}
                                    icon="fa-users"
                                />
                                <NavItem
                                    id="stages"
                                    label={t("divisionTags.stages")}
                                    icon="fa-layer-group"
                                />
                                <NavItem
                                    id="seasons"
                                    label={t("divisionTags.seasons")}
                                    icon="fa-calendar"
                                />
                                <NavItem
                                    id="games"
                                    label={t("divisionTags.schedule")}
                                    icon="fa-list"
                                />
                                <NavItem
                                    id="standings"
                                    label={t("divisionTags.standings")}
                                    icon="fa-trophy"
                                />
                                <NavItem
                                    id="sanctions"
                                    label={t("divisionTags.sanctions")}
                                    icon="fa-gavel"
                                />
                                <NavItem
                                    id="stats"
                                    label={t("divisionTags.stats")}
                                    icon="fa-chart-bar"
                                />

                                {pro && (
                                    <NavItem
                                        id="standings-adjustment"
                                        label={t("divisionTags.pointAdjustments")}
                                        icon="fa-sliders-h"
                                    />
                                )}

                                {!pro && (
                                    <NavItem
                                        id="standings-adjustment"
                                        label={t("divisionTags.pointAdjustments")}
                                        icon="fa-sliders-h"
                                        disabled
                                    />
                                )}
                            </ul>
                        </div>

                        <div style={ui.body}>
                            <div className="tab-content">
                                <Suspense fallback={<div className="text-muted">{t("divisionTags.loading")}</div>}>
                                    {activeTab === "teams" && (
                                        <div className="tab-pane fade show active" id="teams" role="tabpanel">
                                            <TeamList
                                                teams={teams}
                                                activeSeasonData={activeSeasonData}
                                                setTeams={setTeams}
                                                division={division}
                                            />
                                        </div>
                                    )}

                                    {activeTab === "stages" && (
                                        <div className="tab-pane fade show active" id="stages" role="tabpanel">
                                            <StageList
                                                stages={stages}
                                                setStages={setStages}
                                                activeSeasonData={activeSeasonData}
                                                division={division}
                                                waivers={waivers}
                                            />
                                        </div>
                                    )}

                                    {activeTab === "seasons" && (
                                        <div className="tab-pane fade show active" id="seasons" role="tabpanel">
                                            <SeasonList
                                                division={division}
                                                seasons={seasons}
                                                setSeasons={setSeasons}
                                                waivers={waivers}
                                            />
                                        </div>
                                    )}

                                    {activeTab === "games" && (
                                        <div className="tab-pane fade show active" id="games" role="tabpanel">
                                            <Fixtures
                                                stages={stages}
                                                games={games}
                                                setGames={setGames}
                                                division={division}
                                                activeSeasonData={activeSeasonData}
                                                setTotalGames={setTotalGames}
                                            />
                                        </div>
                                    )}

                                    {activeTab === "sanctions" && (
                                        <div className="tab-pane fade show active" id="sanctions" role="tabpanel">
                                            <Sanctions
                                                sanctions={sanctions}
                                                season={activeSeasonData}
                                                games={games.filter((e) => e.eventType === "GAME")}
                                            />
                                        </div>
                                    )}

                                    {activeTab === "standings" && (
                                        <div className="tab-pane fade show active" id="standings" role="tabpanel">
                                            <Standings
                                                standingsByGroup={standings}
                                                division={division}
                                                season={activeSeasonData}
                                            />
                                        </div>
                                    )}

                                    {pro && activeTab === "standings-adjustment" && (
                                        <div
                                            className="tab-pane fade show active"
                                            id="standings-adjustment"
                                            role="tabpanel"
                                        >
                                            <Adjustment
                                                season={activeSeasonData}
                                                teams={teams}
                                                stages={stages}
                                            />
                                        </div>
                                    )}

                                    {activeTab === "stats" && (
                                        <div className="tab-pane fade show active" id="stats" role="tabpanel">
                                            {["soccer", "hockey", "baseball", "softball"].includes(division?.sport?.key) ? (
                                                <StatsDashboard
                                                    sportKey={division.sport?.key}
                                                    isActive={activeTab === "stats"}
                                                    pro={pro}
                                                    seasonId={activeSeasonData?.id}
                                                    stageId={activeSeasonData?.stage?.id || null}
                                                />
                                            ) : (
                                                t("divisionTags.comingSoon")
                                            )}
                                        </div>
                                    )}
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        .kbola-tab-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.08);
        }
      `}</style>
        </>
    );
}
